import { Percent } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

export interface ICommissionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planData: {
    name: string;
    commission_distribution?: Array<{
      id?: string;
      position: number;
      agency: number;
      commission: number;
      pro_labore?: number;
    }>;
    commission_lifetime_other_installments: number;
  } | null;
}

export const CommissionModal: React.FC<ICommissionModalProps> = ({
  open,
  onOpenChange,
  planData,
}) => {
  if (!planData) return null;

  // Verificar se commission_distribution existe e tem dados
  if (
    !planData.commission_distribution ||
    planData.commission_distribution.length === 0
  ) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!w-[80vw] !max-w-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carregamento Comercial - {planData.name}</DialogTitle>
          </DialogHeader>
          <div className="text-center text-gray-500 py-8">
            Nenhum dado de carregamento comercial encontrado para este plano.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const totalAgency = planData.commission_distribution.reduce(
    (sum, item) => sum + (item.agency || 0),
    0
  );

  const totalCommission = planData.commission_distribution.reduce(
    (sum, item) => sum + (item.commission || 0),
    0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[80vw] !max-w-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Carregamento Comercial - {planData.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resumo dos Totais */}
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Comissão (%)
                </CardTitle>
                <Percent className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalCommission}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Agênciamento (%)
                </CardTitle>
                <Percent className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalAgency}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Campos Dinâmicos - Commission Distribution */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Carregamento Comercial</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {planData.commission_distribution.map((item, index) => (
                <Card key={index} className="p-4">
                  <CardTitle className="text-sm font-medium mb-4">
                    {index + 1}º
                  </CardTitle>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Agênciamento (%)
                      </span>
                      <span className="font-medium">{item.agency || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Comissão (%)
                      </span>
                      <span className="font-medium">
                        {item.commission || 0}%
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Campos Fixos - Lifetime Other Installments */}
          <div className="space-y-4">
            <Separator className="my-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-4">
                <CardTitle className="text-sm font-medium mb-4">
                  Demais
                </CardTitle>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Comissão (%)
                  </span>
                  <span className="font-medium">
                    {planData.commission_lifetime_other_installments || 0}%
                  </span>
                </div>
                {/* Pro Labore removido para Planos */}
              </Card>
            </div>
          </div>

          {/* Botão Fechar */}
          <div className="flex justify-end">
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
